import React, { useState, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';
import {
  Box, Typography, Button, Dialog, DialogContent, DialogActions,
  TextField, CircularProgress, Container, DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import API_ROUTES from '../config/api';
import { apiFetch } from '../apiFetch';
import i18n from '../i18n/i18n';
import { logger } from '../services/logger';

const FCM_TOKEN_HASH_KEY = 'srf_fcm_token_hash';

async function hashString(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

interface SessionLevel {
  LEVEL_NAME: string;
}

export interface WizardValues {
  selectedLevels: string[];
  sidePreference: 'any' | 'LEFT' | 'RIGHT';
  preferredDayOfWeek: number[];
  preferredTimeOfDay: string[];
  minSpots: number;
  startTime: number;
  email: string;
  verificationCode: string;
  wantsPush: boolean;
  pushToken: string;
}

interface SessionFinderWizardProps {
  sessionLevels: SessionLevel[];
  initialValues: WizardValues;
  onSubmit: (values: WizardValues) => void;
  isNew?: boolean;
}

const STEP_KEYS = [
  'session', 'side', 'days', 'time', 'spots', 'notice', 'email', 'push', 'finished',
] as const;
type StepKey = typeof STEP_KEYS[number];
const TOTAL_STEPS = STEP_KEYS.length;
const STEP_INDEX = Object.fromEntries(STEP_KEYS.map((k, i) => [k, i])) as Record<StepKey, number>;

const STEP_ICONS: Record<string, string> = {
  session: '🌊', side: '↔', days: '📅', time: '🕐',
  spots: '👥', notice: '🔔', email: '✉', push: '📱', finished: '✅',
};

const WORKING_HOURS_FROM = 6;
const WORKING_HOURS_UNTIL = 22;
function generateTimeOptions(): string[] {
  const opts: string[] = [];
  for (let h = WORKING_HOURS_FROM; h <= WORKING_HOURS_UNTIL; h++) {
    opts.push(`${h.toString().padStart(2, '0')}:00`);
  }
  return opts;
}
const TIME_OPTIONS = generateTimeOptions();

// ── Option card ──────────────────────────────────────────────────────────────
const OptionCard: React.FC<{
  label: string;
  selected: boolean;
  multi: boolean;
  onClick: () => void;
}> = ({ label, selected, multi, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      minHeight: 40,
      px: 2,
      py: 0.75,
      border: selected ? '1.5px solid #0077b6' : '1px solid #d9e2ec',
      borderRadius: '14px',
      background: selected ? '#eef5ff' : '#fff',
      boxShadow: selected ? '0 0 0 3px rgba(0,119,182,0.10)' : 'none',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease, transform 160ms ease',
      '&:hover': { borderColor: selected ? '#0077b6' : '#b8c4d2', background: selected ? '#eef5ff' : '#fbfdff' },
      '&:active': { transform: 'scale(0.99)' },
    }}
  >
    <Typography sx={{ fontSize: '1rem', fontWeight: selected ? 700 : 400, color: '#101828', lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Box sx={{
      width: 20, height: 20, flexShrink: 0,
      border: selected ? '1.5px solid #0077b6' : '1.5px solid #b8c4d2',
      borderRadius: multi ? '5px' : '50%',
      background: selected ? '#0077b6' : 'transparent',
      display: 'grid', placeItems: 'center',
      color: '#fff', fontSize: 12, fontWeight: 800,
      transition: 'all 160ms ease',
    }}>
      {selected && '✓'}
    </Box>
  </Box>
);

// ── Time chip ────────────────────────────────────────────────────────────────
const TimeChip: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 2, minHeight: 40, px: 2, py: 0.75,
      borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
      border: selected ? '1.5px solid #0077b6' : '1px solid #d9e2ec',
      background: selected ? '#eef5ff' : '#fff',
      boxShadow: selected ? '0 0 0 3px rgba(0,119,182,0.10)' : 'none',
      transition: 'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease, transform 160ms ease',
      '&:hover': { borderColor: selected ? '#0077b6' : '#b8c4d2', background: selected ? '#eef5ff' : '#fbfdff' },
      '&:active': { transform: 'scale(0.97)' },
    }}
  >
    <Typography sx={{ fontSize: '0.9rem', fontWeight: selected ? 700 : 400, color: '#101828', lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Box sx={{
      width: 20, height: 20, flexShrink: 0,
      border: selected ? '1.5px solid #0077b6' : '1.5px solid #b8c4d2',
      borderRadius: '5px',
      background: selected ? '#0077b6' : 'transparent',
      display: 'grid', placeItems: 'center',
      color: '#fff', fontSize: 12, fontWeight: 800,
      transition: 'all 160ms ease',
    }}>
      {selected && '✓'}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const SessionFinderWizard: React.FC<SessionFinderWizardProps> = ({
  sessionLevels, initialValues, onSubmit, isNew = false,
}) => {
  const { t: tOrig } = useTranslation();
  const t = (key: string, opts?: any) => {
    const val = tOrig(key, opts);
    return val === key ? `***${key}***` : val;
  };

  const direction = i18n.language === 'he' ? 'rtl' : 'ltr';

  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardValues>(initialValues);
  const [emailError, setEmailError] = useState('');
  const [showSessionError, setShowSessionError] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showFcmInitDialog, setShowFcmInitDialog] = useState(false);
  const [showFcmErrorDialog, setShowFcmErrorDialog] = useState(false);
  const [fcmErrorDetails, setFcmErrorDetails] = useState<string | null>(null);
  const [emailJumpedToSummary, setEmailJumpedToSummary] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [previewSessions, setPreviewSessions] = useState<any[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setData(initialValues);
    setStep(0);
    setEmailError('');
    setShowSessionError(false);
    setPushMessage(null);
    setMessage(null);
    setShowAlert(false);
    setEmailJumpedToSummary(false);
  }, [initialValues]);

  const formatPreviewDayLabel = (session: any) => {
    const today = new Date();
    const sessionDate = new Date(session.SESSION_DATETIME_UTCISO);
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const sessionMidnight = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
    const diffDays = Math.floor((sessionMidnight.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24));

    const dayNames = [
      t('days.sunday'),
      t('days.monday'),
      t('days.tuesday'),
      t('days.wednesday'),
      t('days.thursday'),
      t('days.friday'),
      t('days.saturday'),
    ];
    const dayOfWeek = dayNames[sessionDate.getDay()];

    if (diffDays === 0) return `${t('sessions.today')}, ${dayOfWeek}`;
    if (diffDays === 1) return `${t('sessions.tomorrow')}, ${dayOfWeek}`;
    if (diffDays === 2) return `${t('sessions.dayAfterTomorrow')}, ${dayOfWeek}`;
    if (diffDays > 2) return `${t('sessions.inXDays', { diffDays })}, ${dayOfWeek}`;

    logger.warn('Unexpected diffDays in wizard preview', { diffDays, datetime: session.SESSION_DATETIME_UTCISO });
    return '';
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePushToggle = async (enable: boolean) => {
    setData(d => ({ ...d, pushToken: '' }));
    setPushMessage(null);
    if (!enable) {
      setData(d => ({ ...d, wantsPush: false, pushToken: '' }));
      return;
    }
    setShowFcmInitDialog(true);
    setPushLoading(true);
    try {
      logger.info('FCM: Requesting token (wizard)...');
      const win = window as any;
      if (!win.getFcmToken) {
        const errMsg = t('push.fcmNotDefined');
        logger.error('FCM: ' + errMsg);
        setFcmErrorDetails(errMsg);
        setShowFcmErrorDialog(true);
        return;
      }
      const token = await win.getFcmToken();
      logger.info('FCM: Token result:', token);
      if (!token) {
        const errMsg = t('push.fcmDenied');
        logger.error('FCM: ' + errMsg);
        setFcmErrorDetails(errMsg);
        setShowFcmErrorDialog(true);
        return;
      }
      const hash = await hashString(token);
      localStorage.setItem(FCM_TOKEN_HASH_KEY, hash);
      setData(d => ({ ...d, wantsPush: true, pushToken: token }));
    } catch (e: any) {
      logger.error('FCM: Exception during registration', e);
      setFcmErrorDetails(e?.message || String(e));
      setShowFcmErrorDialog(true);
    } finally {
      setShowFcmInitDialog(false);
      setPushLoading(false);
    }
  };

  const handleTestPushClick = async () => {
    setPushLoading(true);
    setPushMessage(null);
    for (let secondsLeft = 5; secondsLeft > 0; secondsLeft--) {
      // eslint-disable-next-line no-await-in-loop
      if (secondsLeft > 1) await new Promise(resolve => setTimeout(resolve, 1000));
    }
    try {
      const res = await apiFetch(API_ROUTES.sendPushNotification, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fcm_token: data.pushToken,
          channel: 'EMAIL',
          channel_user_identity: data.email,
          device_info: navigator.userAgent,
        }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setPushMessage({ type: 'success', text: t('dialogs.push.testSent') });
      } else {
        setPushMessage({ type: 'error', text: result.message || t('dialogs.push.testError') });
        setData(d => ({ ...d, wantsPush: false, pushToken: '' }));
      }
    } catch {
      setPushMessage({ type: 'error', text: t('dialogs.push.testError') });
      setData(d => ({ ...d, wantsPush: false, pushToken: '' }));
    } finally {
      setPushLoading(false);
    }
  };

  const handlePreviewSessions = async () => {
    setPreviewLoading(true);
    try {
      const sidePrefs = data.sidePreference === 'any' ? [] : [data.sidePreference];
      const response = await apiFetch(API_ROUTES.sessionsFilter, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          CHANNEL: 'EMAIL',
          CHANNEL_USER_IDENTITY: data.email || 'preview@example.com',
          VERIFICATION_CODE: 'preview',
          DAYS_AHEAD: -1,
          FOR_LEVELS: data.selectedLevels,
          SIDE_PREFERENCE: sidePrefs,
          PREFERRED_DAY_OF_WEEK: data.preferredDayOfWeek,
          AVAILABLE_MIN: data.minSpots,
          EXPIRY_PERIOD: 4,
          START_TIME: data.startTime,
          PREFERRED_TIME_OF_DAY: data.preferredTimeOfDay,
        }),
      });
      const sessions = await response.json();
      if (response.ok) {
        setPreviewSessions(sessions);
        setShowPreviewDialog(true);
      } else {
        const errorText = typeof sessions?.detail === 'string'
          ? sessions.detail
          : sessions?.detail?.message || t('dialogs.preview.error');
        setMessage({ type: 'error', text: errorText });
        setShowAlert(true);
      }
    } catch (e) {
      logger.error('Preview sessions error', e);
      setMessage({ type: 'error', text: t('error.general') });
      setShowAlert(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleNext = () => {
    if (step === STEP_INDEX.session) {
      if (data.selectedLevels.length === 0) {
        setShowSessionError(true);
        return;
      }
    }
    if (step === STEP_INDEX.email) {
      if (!data.email || !validateEmail(data.email)) {
        setEmailError(t('form.invalidEmail'));
        return;
      }
      setEmailError('');
      if (isNew && !emailJumpedToSummary) {
        setEmailJumpedToSummary(true);
        setStep(STEP_INDEX.finished);
        return;
      }
    }
    setStep(s => {
      const next = Math.min(s + 1, STEP_INDEX.finished);
      return isNew && !emailJumpedToSummary && next === STEP_INDEX.push ? next + 1 : next;
    });
  };

  const handleBack = () => setStep(s => {
    const prev = Math.max(s - 1, 0);
    return isNew && !emailJumpedToSummary && prev === STEP_INDEX.push ? prev - 1 : prev;
  });
  const handleSubmit = () => onSubmit(data);

  const handleFinishClick = () => {
    if (isLastStep) { handleSubmit(); return; }
    const skipPush = isNew && !emailJumpedToSummary;
    const dest = STEP_INDEX.finished;
    let curr = step;
    let landing = step;
    while (curr < dest) {
      if (!isStepValid(curr)) break;
      let next = curr + 1;
      if (skipPush && next === STEP_INDEX.push) next++;
      landing = Math.min(next, dest);
      curr = next;
    }
    setStep(landing);
    if (landing < dest) triggerValidationUX(landing);
    else onSubmit(data);
  };

  const stepKey = STEP_KEYS[step];
  const stepTitle = t(`wizard.steps.${stepKey}`);
  const isLastStep = step === STEP_INDEX.finished;
  const progressPct = Math.round(((step + 1) / TOTAL_STEPS) * 100);

  const dayNames = [
    '', t('days.sunday'), t('days.monday'), t('days.tuesday'), t('days.wednesday'),
    t('days.thursday'), t('days.friday'), t('days.saturday'),
  ];

  // ── Summary values ───────────────────────────────────────────────────────
  const levelsText = data.selectedLevels.length === 0 ? t('summary.allLevels') : data.selectedLevels.join(` ${t('summary.or')} `);
  const sideText = data.sidePreference === 'any' ? t('summary.sideAny') : data.sidePreference === 'LEFT' ? t('summary.sideLeft') : t('summary.sideRight');
  const daysText = data.preferredDayOfWeek.includes(0) ? t('summary.anyDay') : data.preferredDayOfWeek.map(d => dayNames[d]).join(` ${t('summary.or')} `);
  const timeText = data.preferredTimeOfDay.length === 0 ? t('summary.anyTime') : [...data.preferredTimeOfDay].sort().join(', ');
  const spotsText = data.minSpots === 0 ? t('form.spotsAny') : data.minSpots === 2 ? t('form.spots2') : data.minSpots === 5 ? t('form.spots5') : t('form.spots10');
  const noticeText = data.startTime === 168 ? t('form.noticeWeek') : data.startTime === 48 ? t('form.notice48h') : data.startTime === 24 ? t('form.notice24h') : data.startTime === 12 ? t('form.notice12h') : t('form.notice2h');

  // ── Step content ─────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (stepKey) {
      case 'session':
        return (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {sessionLevels.map(level => (
              <OptionCard
                key={level.LEVEL_NAME}
                label={level.LEVEL_NAME}
                selected={data.selectedLevels.includes(level.LEVEL_NAME)}
                multi={true}
                onClick={() => {
                  const sel = data.selectedLevels.includes(level.LEVEL_NAME)
                    ? data.selectedLevels.filter(l => l !== level.LEVEL_NAME)
                    : [...data.selectedLevels, level.LEVEL_NAME];
                  setData(d => ({ ...d, selectedLevels: sel }));
                }}
              />
            ))}
            {sessionLevels.length === 0 && (
              <Typography variant="body2" color="text.secondary">{t('form.sessionLevelsError')}</Typography>
            )}
          </Box>
        );

      case 'side':
        return (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {(['any', 'RIGHT', 'LEFT'] as const).map(val => (
              <OptionCard
                key={val}
                label={val === 'any' ? t('form.sideAny') : val === 'RIGHT' ? t('form.sideRight') : t('form.sideLeft')}
                selected={data.sidePreference === val}
                multi={false}
                onClick={() => setData(d => ({ ...d, sidePreference: val }))}
              />
            ))}
          </Box>
        );

      case 'days': {
        const allSelected = data.preferredDayOfWeek.includes(0);
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <OptionCard
                label={t('form.allDays')}
                selected={allSelected}
                multi={false}
                onClick={() => setData(d => ({ ...d, preferredDayOfWeek: [0] }))}
              />
            </Box>
            {[1, 2, 3, 4, 5, 6, 7].map(day => (
              <OptionCard
                key={day}
                label={dayNames[day]}
                selected={data.preferredDayOfWeek.includes(day)}
                multi={true}
                onClick={() => {
                  let next = [...data.preferredDayOfWeek];
                  if (next.includes(day)) {
                    next = next.filter(d => d !== day);
                  } else {
                    next = next.filter(d => d !== 0);
                    next.push(day);
                  }
                  if (next.length === 0) next = [0];
                  setData(d => ({ ...d, preferredDayOfWeek: next }));
                }}
              />
            ))}
          </Box>
        );
      }

      case 'time':
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TimeChip
                label={t('summaryEx.anyTime')}
                selected={data.preferredTimeOfDay.length === 0}
                onClick={() => setData(d => ({ ...d, preferredTimeOfDay: [] }))}
              />
            </Box>
            {TIME_OPTIONS.map(opt => (
              <TimeChip
                key={opt}
                label={opt}
                selected={data.preferredTimeOfDay.includes(opt)}
                onClick={() => {
                  const next = data.preferredTimeOfDay.includes(opt)
                    ? data.preferredTimeOfDay.filter(t => t !== opt)
                    : [...data.preferredTimeOfDay, opt];
                  setData(d => ({ ...d, preferredTimeOfDay: next }));
                }}
              />
            ))}
          </Box>
        );

      case 'spots':
        return (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {([0, 10, 5, 2] as const).map(val => (
              <OptionCard
                key={val}
                label={val === 0 ? t('form.spotsAny') : val === 10 ? t('form.spots10') : val === 5 ? t('form.spots5') : t('form.spots2')}
                selected={data.minSpots === val}
                multi={false}
                onClick={() => setData(d => ({ ...d, minSpots: val }))}
              />
            ))}
          </Box>
        );

      case 'notice':
        return (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {([2, 12, 24, 48, 168] as const).map(val => (
              <OptionCard
                key={val}
                label={val === 168 ? t('form.noticeWeek') : val === 48 ? t('form.notice48h') : val === 24 ? t('form.notice24h') : val === 12 ? t('form.notice12h') : t('form.notice2h')}
                selected={data.startTime === val}
                multi={false}
                onClick={() => setData(d => ({ ...d, startTime: val }))}
              />
            ))}
          </Box>
        );

      case 'email':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#101828' }}>
                {t('form.email')}
              </Typography>
              <TextField
                type="email"
                value={data.email}
                onChange={e => {
                  const v = e.target.value;
                  setData(d => ({ ...d, email: v, verificationCode: '' }));
                  if (v && !validateEmail(v)) setEmailError(t('form.invalidEmail'));
                  else setEmailError('');
                }}
                error={!!emailError}
                helperText={emailError}
                autoComplete="email"
                fullWidth
                inputProps={{ style: { height: 52, padding: '0 14px', boxSizing: 'border-box', fontSize: 16 } }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0077b6',
                    boxShadow: '0 0 0 3px rgba(0,119,182,0.10)',
                  },
                }}
              />
            </Box>
            {(!isNew || emailJumpedToSummary) && <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#101828' }}>
                {t('form.code')}
              </Typography>
              <TextField
                type="text"
                value={data.verificationCode}
                onChange={e => setData(d => ({ ...d, verificationCode: e.target.value }))}
                placeholder={t('form.codePlaceholder')}
                fullWidth
                inputProps={{ style: { height: 52, padding: '0 14px', boxSizing: 'border-box', fontSize: 16 } }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0077b6',
                    boxShadow: '0 0 0 3px rgba(0,119,182,0.10)',
                  },
                }}
              />
            </Box>}
          </Box>
        );

      case 'push':
        return (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <OptionCard
              label={t('wizard.push.label')}
              selected={data.wantsPush}
              multi={true}
              onClick={() => !pushLoading && handlePushToggle(!data.wantsPush)}
            />
            {data.pushToken && !pushLoading && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleTestPushClick}
                sx={{ width: 'fit-content' }}
              >
                {t('push.sendTest')}
              </Button>
            )}
            {pushMessage && (
              <Typography variant="body2" color={pushMessage.type === 'success' ? 'success.main' : 'error.main'} sx={{ px: 0.5 }}>
                {pushMessage.text}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, px: 0.5 }}>
              {t('wizard.push.hint')}
            </Typography>
            {/* FCM Initializing Dialog */}
            <Dialog open={showFcmInitDialog} onClose={() => {}} maxWidth="xs" fullWidth PaperProps={{ style: { pointerEvents: 'none' } }}>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body1" sx={{ mt: 2 }}>{t('dialogs.fcmInitDialog.trying')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('dialogs.fcmInitDialog.prompt')}</Typography>
                </Box>
              </DialogContent>
            </Dialog>
            {/* FCM Error Dialog */}
            <Dialog open={showFcmErrorDialog} onClose={() => setShowFcmErrorDialog(false)} maxWidth="sm" fullWidth>
              <DialogContent>
                {fcmErrorDetails && (
                  <Box sx={{ mt: 2, background: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">{fcmErrorDetails}</Typography>
                  </Box>
                )}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('push.fcmErrorDialog.desc')}
                    <br />
                    <strong>{t('push.fcmErrorDialog.step1')}</strong> <span style={{ fontSize: '1.2em' }}>🔒</span> <strong>{t('push.fcmErrorDialog.step2')}</strong>
                    <br />
                    {t('push.fcmErrorDialog.step3')}
                    <br />
                    {t('push.fcmErrorDialog.step4')}
                  </Typography>
                  <Button variant="outlined" onClick={() => window.open('https://www.google.com/search?q=enable+notifications+for+site+in+browser', '_blank')}>
                    {t('push.fcmErrorDialog.helpBtn')}
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={() => setShowFcmErrorDialog(false)} variant="contained">{t('push.fcmErrorDialog.close')}</Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 'finished': {
        const rows: { label: string; value: string; goTo: number }[] = [
          { label: t('wizard.steps.session'), value: levelsText,  goTo: STEP_INDEX.session },
          { label: t('wizard.steps.side'),    value: sideText,    goTo: STEP_INDEX.side },
          { label: t('wizard.steps.days'),    value: daysText,    goTo: STEP_INDEX.days },
          { label: t('wizard.steps.time'),    value: timeText,    goTo: STEP_INDEX.time },
          { label: t('wizard.steps.spots'),   value: spotsText,   goTo: STEP_INDEX.spots },
          { label: t('wizard.steps.notice'),  value: noticeText,  goTo: STEP_INDEX.notice },
          { label: t('wizard.steps.email'),   value: data.email,  goTo: STEP_INDEX.email },
          { label: t('wizard.steps.push'),    value: data.wantsPush ? '✓' : '—', goTo: STEP_INDEX.push },
        ];
        return (
          <Box>
            <Box sx={{
              border: '1px solid #d9e2ec',
              borderRadius: '20px',
              background: '#fff',
              boxShadow: '0 12px 30px rgba(16,24,40,0.08)',
              overflow: 'hidden',
            }}>
              {rows.map((row, i) => (
                <Box key={row.label} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2, px: 2, py: 1.5,
                  borderBottom: i < rows.length - 1 ? '1px solid #d9e2ec' : 'none',
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: '#667085', mb: 0.3 }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#101828', wordBreak: 'break-word' }}>{row.value}</Typography>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => setStep(row.goTo)}
                    sx={{
                      border: 0, background: 'transparent', color: '#0077b6',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0, p: 0,
                    }}
                  >
                    {t('wizard.edit')}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  // ── Shared sub-elements ──────────────────────────────────────────────────────
  const isStepValid = (i: number): boolean => {
    switch (STEP_KEYS[i]) {
      case 'session': return data.selectedLevels.length > 0;
      case 'email': return !!data.email && validateEmail(data.email);
      default: return true;
    }
  };

  const triggerValidationUX = (i: number) => {
    switch (STEP_KEYS[i]) {
      case 'session': setShowSessionError(true); break;
      case 'email': setEmailError(t('form.invalidEmail')); break;
      default: break;
    }
  };

  const handleStepClick = (target: number) => {
    const skipPush = isNew && !emailJumpedToSummary;
    let dest = skipPush && target === STEP_INDEX.push
      ? (step < STEP_INDEX.push ? STEP_INDEX.push + 1 : STEP_INDEX.push - 1)
      : target;
    if (dest === step) return;
    if (dest < step) { setStep(dest); return; }
    // forward: advance until first invalid step or target
    let curr = step;
    let landing = step;
    while (curr < dest) {
      if (!isStepValid(curr)) break;
      let next = curr + 1;
      if (skipPush && next === STEP_INDEX.push) next++;
      landing = Math.min(next, dest);
      curr = next;
    }
    setStep(landing);
    if (landing < dest) triggerValidationUX(landing);
  };

  const progressBar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <Box
          key={i}
          onClick={() => handleStepClick(i)}
          title={t(`wizard.steps.${STEP_KEYS[i]}`)}
          sx={{
            flex: 1, height: 4, borderRadius: 999,
            background: i <= step ? '#0077b6' : '#dce6f0',
            transition: 'background 220ms ease',
            cursor: 'pointer',
            '&:hover': { background: i <= step ? '#005a8c' : '#b8c8d8' },
          }}
        />
      ))}
      <Typography sx={{ fontSize: 13, color: '#0077b6', fontWeight: 700, ml: 0.5, flexShrink: 0 }}>
        {step + 1}/{TOTAL_STEPS}
      </Typography>
    </Box>
  );

  const btnBase = {
    height: 40, borderRadius: '10px', fontSize: 13, fontWeight: 700,
    textTransform: 'none' as const,
    '&:active': { transform: 'scale(0.98)' },
  };

  const navButtons = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
      {/* Prev */}
      <Button disabled={step === 0} onClick={handleBack} variant="outlined" sx={{ ...btnBase, color: '#0077b6', borderColor: '#0077b6', background: '#fff', '&:hover': { background: '#eef5ff', borderColor: '#0077b6' } }}>
        {t('wizard.back')}
      </Button>

      {/* Next */}
      <Button disabled={isLastStep} onClick={handleNext} variant="contained" sx={{ ...btnBase, background: '#90be6d', color: '#fff', '&:hover': { background: '#76a855' } }}>
        {t('wizard.next')}
      </Button>

      {/* Test */}
      <Button onClick={handlePreviewSessions} disabled={previewLoading} variant="outlined" sx={{ ...btnBase, color: '#0077b6', borderColor: '#0077b6', '&:hover': { background: '#eef5ff' } }}>
        {previewLoading ? <CircularProgress size={16} /> : t('wizard.test')}
      </Button>

      {/* Finish / Submit */}
      <Button
        onClick={handleFinishClick}
        variant="contained"
        sx={{ ...btnBase, background: '#90be6d', color: '#fff', '&:hover': { background: '#76a855' } }}
      >
        {t('wizard.finish')}
      </Button>
    </Box>
  );

  const sessionErrorDialog = (
    <Dialog open={showSessionError} onClose={() => setShowSessionError(false)} maxWidth="sm" fullWidth dir={direction}>
      <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          {t('form.selectAtLeastOneSession')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button onClick={() => setShowSessionError(false)} variant="contained" sx={{ textTransform: 'none' }}>
          {t('dialogs.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
    <Container
        maxWidth="sm"
        dir={direction}
        sx={{
          py: 0,
          backgroundColor: '#fff',
          borderRadius: '20px',
          boxShadow: '0px 4px 20px rgba(0,0,0,0.14)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
        }}
      >
        {/* ── Page header ── */}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(175deg, #002d6e 0%, #004ea3 50%, #0077b6 100%)',
            color: '#fff', pt: 3, pb: '30px', px: 3,
          }}
          dir={direction}
        >
          {/* Text content — sits above SVG and overlay */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            {/* Logo + brand name row — left-aligned */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <a
                href={process.env.REACT_APP_WWW_HOME}
                title={process.env.REACT_APP_WWW_HOME}
                style={{ textDecoration: 'none', flexShrink: 0 }}
                rel="noopener noreferrer"
              >
                <Box
                  component="img"
                  src={process.env.PUBLIC_URL + '/favicon.svg'}
                  alt="Surf Session Finder Logo"
                  sx={{ width: 36, height: 36, borderRadius: '50%', display: 'block' }}
                />
              </a>
              <Typography component="a" href={process.env.REACT_APP_WWW_HOME} rel="noopener noreferrer" sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none' }}>
                Surf Session Finder
              </Typography>
            </Box>

            {/* Title */}
            <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '1.55rem', sm: '2rem' }, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1 }}>
              {process.env.REACT_APP_HEADER_TITLE}
            </Typography>

            {/* Subtitle */}
            <Typography component="h2" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.85)' }}>
              {process.env.REACT_APP_HEADER_SUBTITLE}
            </Typography>
          </Box>

          {/* Surfer + wave asset */}
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/images/surfer.svg'}
            alt=""
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: 'auto',
              display: 'block',
              pointerEvents: 'none',
              zIndex: 0,
              transform: direction === 'rtl' ? 'scaleX(1)' : 'scaleX(-1)',
              opacity: 0.85,
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
            }}
          />

          {/* Dark overlay — sits above SVG, below text — darkens wave for text readability */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: 'linear-gradient(to bottom, rgba(0,20,70,0.92) 0%, rgba(0,30,85,0.6) 35%, rgba(0,40,100,0.45) 60%, rgba(0,50,110,0.55) 82%, transparent 100%)',
          }} />
        </Box>

        {/* ── Step header ── */}
        <Box sx={{ px: 2.5, pb: 1.5, pt: 1 }}>
          <Box sx={{ mb: 1.5 }}>
            {progressBar}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(0,119,182,0.12)', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>
              {STEP_ICONS[stepKey]}
            </Box>
            <Typography component="h3" sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, color: '#101828', letterSpacing: '-0.03em', lineHeight: 1.2 }}>
              {stepTitle}
            </Typography>
          </Box>
        </Box>

        {/* ── Step content ── */}
        <Box sx={{ px: 2.5, pt: 0.5, pb: 2, flex: 1 }}>
          {renderStep()}
        </Box>

        {/* ── Sticky nav + footer strip ── */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(14px)',
          borderTop: '1px solid rgba(217,226,236,0.8)',
        }}>
          <Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
            {navButtons}
          </Box>
          <Box sx={{
            background: '#f4f6f9',
            px: 2.5,
            pt: 0.75,
            pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            textAlign: 'center',
            borderRadius: '0 0 20px 20px',
          }}>
            <Typography sx={{ fontSize: 11, color: '#9aaab8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
              <a href={process.env.REACT_APP_WWW_HOME} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{t('wizard.footer.freeService')}</a>
              <span>·</span>
              <a href={`https://www.paypal.com/donate?business=CPJ95856Q2KGN&currency_code=${process.env.REACT_APP_DONATE_CURRENCY || 'USD'}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{t('wizard.footer.donate')}</a>
              <span>·</span>
              <a href="https://www.surfsessionfinder.com/contact/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{t('wizard.footer.contact')}</a>
            </Typography>
          </Box>
        </Box>
      </Container>
      {sessionErrorDialog}
      <Dialog open={showAlert} onClose={() => setShowAlert(false)} maxWidth="sm" fullWidth dir={direction}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          {message?.type === 'success' ? t('dialogs.success.title') : t('dialogs.error.title')}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', py: 2 }}
            component="div"
            dangerouslySetInnerHTML={
              message?.text
                ? { __html: sanitizeHtml(message.text, { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } }) }
                : undefined
            }
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => setShowAlert(false)}
            variant="contained"
            color={message?.type === 'success' ? 'success' : 'error'}
          >
            {t('dialogs.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showPreviewDialog} onClose={() => setShowPreviewDialog(false)} maxWidth="md" fullWidth dir={direction}>
        <DialogContent>
          {previewSessions.length === 0 ? (
            <Typography
              variant="body1"
              component="div"
              sx={{ textAlign: 'center', py: 3 }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(t('dialogs.preview.none'), { allowedTags: ['b', 'br'], allowedAttributes: { b: [] } }),
              }}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                {t('dialogs.preview.found', { count: previewSessions.length })}
              </Typography>
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {previewSessions.map((session: any, index: number) => (
                  <Box
                    key={session.ID}
                    sx={{ p: 1, mb: 0.5, border: '1px solid #ddd', borderRadius: 1, backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff', cursor: 'pointer', transition: 'background 0.2s', '&:hover': { backgroundColor: '#e0f7fa' } }}
                    onClick={() => {
                      const url = session.EXT_URL;
                      if (url) {
                        window.open(url, '_blank');
                      } else {
                        logger.error('Wizard preview session missing EXT_URL');
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.2 }}>
                          {session.SESSION_TITLE} - {session.SESSION_SIDE}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1 }} color="text.secondary">
                          {formatPreviewDayLabel(session)} {formatPreviewDayLabel(session) ? ' ' : ''}{session.SESSION_DATE}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1 }} color="text.secondary">
                          {t('sessions.at')} {session.SESSION_TIME}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: session.AVAILABLE_SPOTS >= 10 ? 'green' : session.AVAILABLE_SPOTS >= 5 ? 'orange' : 'red' }}>
                          {session.AVAILABLE_SPOTS} {t('sessions.spots')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setShowPreviewDialog(false)} variant="contained">{t('dialogs.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionFinderWizard;